import React, { useContext, useEffect, useState } from 'react';

import styled from '@emotion/styled';

import {
  RatedDriverSortByDataType,
  RatedDriverSortBySchema,
  RatedDriverSortBySchemaInitialValues,
} from '../../../models/dashboard/sort-forms/rated-driver-sort-by.model';
import CustomBlueSelectField from '../../FormCom/CustomBlueSelectField';
import { FormComponent } from '../../FormCom/FormComponent';
import SortIcon from '../../Icons/SortIcon';
import { TableContext } from '../TableContext';

interface ToggleThenAddToQueryProps {
  sortField: string[];
  // Maps each selectable label to the boolean query key it should set to 'true' (all other mapped keys are set to 'false')
  sortValueMap: Record<string, string>;
}

const ToggleThenAddToQuery: React.FC<ToggleThenAddToQueryProps> = ({
  sortField,
  sortValueMap,
}) => {
  const tableContext = useContext(TableContext);
  const [formInitialState, setFormInitialState] =
    useState<RatedDriverSortByDataType>({});

  const queryKeys = Object.values(sortValueMap);
  // 'default' is only a clear signal, never an actual query param
  const realQueryKeys = queryKeys.filter((key) => key !== 'default');

  const buildToggleFilter = (selectedLabel: string) => {
    const selectedKey = sortValueMap[selectedLabel];

    // 'default' clears every sort key instead of being sent as its own query param
    if (selectedKey === 'default') {
      return queryKeys.reduce<Record<string, string>>((acc, key) => {
        acc[key] = '';
        return acc;
      }, {});
    }

    return realQueryKeys.reduce<Record<string, string>>((acc, key) => {
      acc[key] = String(key === selectedKey);
      return acc;
    }, {});
  };

  useEffect(() => {
    setFormInitialState({
      ...RatedDriverSortBySchemaInitialValues,
      ...tableContext?.tableSortSelectedFilter,
    });
  }, [tableContext?.tableSortSelectedFilter]);

  if (Object.keys(formInitialState).length === 0) {
    return <div>'...'</div>;
  }

  const handleOnSubmit = (values: RatedDriverSortByDataType) => {
    if (!values.sortBy) return;

    tableContext?.setTableSortSelectedFilter(
      (prevFilter: RatedDriverSortByDataType) => ({
        ...prevFilter,
        ...values,
      }),
    );
    tableContext?.handleSetTableFilterRecord({
      ...buildToggleFilter(values.sortBy),
      page: 0,
    });
  };

  const handleFieldChange = () => {
    return (value: string) => {
      setFormInitialState((prevFilter) => ({
        ...prevFilter,
        sortBy: value,
      }));
      tableContext?.setTableSortSelectedFilter(
        (prevFilter: RatedDriverSortByDataType) => ({
          ...prevFilter,
          sortBy: value,
        }),
      );

      tableContext?.handleSetTableFilterRecord({
        ...buildToggleFilter(value),
        page: 0,
      });
    };
  };

  return (
    <FormComponent
      initialValues={formInitialState}
      schema={RatedDriverSortBySchema}
      onSubmit={handleOnSubmit}
    >
      <CustomWidth>
        <CustomBlueSelectField
          name='sortBy'
          id='sortBy'
          placeholder='Sort By'
          onChange={handleFieldChange()}
          optionData={sortField}
          customIcon={<SortIcon />}
        />
      </CustomWidth>
    </FormComponent>
  );
};

export default ToggleThenAddToQuery;

const CustomWidth = styled.div`
  width: 180px;
`;
